import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { TIMEZONE_GROUPS } from "@/lib/timezones";

interface TimezoneSelectProps {
  value: string;
  onValueChange: (tz: string) => void;
  /** Applied to the trigger — size / width overrides. */
  className?: string;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
}

/**
 * Reusable timezone picker — a grouped dropdown over the curated main
 * zones per continent (with flags + live GMT offsets), plus the browser's
 * own zone pinned under "Detected" when it isn't curated. See
 * `src/lib/timezones`. Built on the shadcn Select so it scrolls natively
 * inside dialogs. Replaces every hand-rolled timezone dropdown so the
 * vocabulary stays in lock-step.
 */
export function TimezoneSelect({
  value,
  onValueChange,
  className,
  disabled,
  id,
  "aria-label": ariaLabel = "Timezone",
}: TimezoneSelectProps) {
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger
        id={id}
        aria-label={ariaLabel}
        className={cn("h-10 rounded-lg border-slate-200 bg-white", className)}
      >
        <SelectValue placeholder="Select timezone" />
      </SelectTrigger>
      <SelectContent className="max-h-[420px]">
        {TIMEZONE_GROUPS.map((g, gi) => (
          <SelectGroup key={g.label}>
            {gi > 0 && <SelectSeparator />}
            <SelectLabel className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              {g.label}
            </SelectLabel>
            {g.zones.map((z) => (
              <SelectItem key={z.value} value={z.value}>
                {z.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
